import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { useNotifyOnError } from '~/hooks/useNotifyOnError'

import { dialogInfoAtom } from '../atoms'
import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useRemoveBrandAccountAllowDomainMutation } from '../hooks/useRemoveBrandAccountAllowDomainMutation'

export const DeleteAllowedDomainDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  if (dialogInfo?.dialogType !== 'delete') {
    throw new Error()
  }

  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const notify = useNotify()
  const notifyError = useNotifyOnError()
  const brandAccountId = useBrandAccountId()
  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()

  const removeBrandAccountAllowDomainMutation =
    useRemoveBrandAccountAllowDomainMutation({
      onSuccess: (data) => {
        if (data.removeBrandAccountAllowDomain?.result !== true) {
          notify({
            style: 'error',
            content: 'ドメインの削除に失敗しました',
          })
          resetDialogInfo()

          return
        }

        notify({
          style: 'success',
          content: `${dialogInfo.domain.value}を削除しました`,
        })
        logEvent('Delete allowed domain')
        closeDialogWithRefresh()
      },
      onError: (error) => {
        notifyError(error, {
          403: '許可ドメインを削除する権限がありません',
        })
        resetDialogInfo()
      },
    })

  const handleExecute = React.useCallback(() => {
    removeBrandAccountAllowDomainMutation.mutate({
      brandAccountId,
      allowDomain: dialogInfo.domain.value,
    })
  }, [
    brandAccountId,
    dialogInfo.domain.value,
    removeBrandAccountAllowDomainMutation,
  ])

  const handleCancel = React.useCallback(() => {
    resetDialogInfo()
  }, [resetDialogInfo])

  return (
    <ExecuteCancelDialog
      executing={removeBrandAccountAllowDomainMutation.isPending}
      mode="delete"
      title="ドメインを削除"
      onCancel={handleCancel}
      onExecute={handleExecute}
    >
      {t('delete_confirmation', { target: dialogInfo.domain.value })}
    </ExecuteCancelDialog>
  )
}
